import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ChatInterface.css";
import { apiBaseUrl } from "../config/environment";
import { useChat } from "../context/ChatContext";
import { useSettings } from "../context/SettingsContext";

// SVG icons for voice actions
const LockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 11H6C4.89543 11 4 11.8954 4 13V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V13C20 11.8954 19.1046 11 18 11Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CallIcon = () => (
  <img
    src="/icon-call.png"
    alt="Call"
    width="20"
    height="20"
    style={{ display: "block" }}
  />
);

const TalkIcon = () => (
  <img
    src="/icon-talk.png"
    alt="Talk"
    width="20"
    height="20"
    style={{ display: "block" }}
  />
);

const ChevronIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 15L12 9L6 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 10V12C19 15.87 15.87 19 12 19M5 10V12C5 15.87 8.13 19 12 19M12 19V23M8 23H16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VOICE_ACTIONS = [
  // The chevron occupies one vertical slot in the rail, so action centers
  // are offset by one step from the original values.
  { id: "lock", label: "Continue recording...", icon: LockIcon, y: 112 },
  { id: "talk", label: "Talk now", icon: TalkIcon, y: 168 },
  { id: "call", label: "Get on a call", icon: CallIcon, y: 224 },
];

const GESTURE_THRESHOLDS = {
  deadZone: 20,
  intentLock: 40,
  enterSelection: 70,
  stickySnap: 35,
  cancelLeft: 60,
};

const ChatInterface = () => {
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingTextMessage, setIsSendingTextMessage] = useState(false);
  const [response, setResponse] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // Add message history state
  const [messageHistory, setMessageHistory] = useState([]);
  const [isChatBarVisible] = useState(true);
  const [isChatBarDisabled, setIsChatBarDisabled] = useState(false);
  const [voiceGesture, setVoiceGesture] = useState({
    mode: "idle", // idle | recording | selection | cancel
    intent: null, // null | vertical | horizontal
    selectedAction: null,
    dragUp: 0,
    dragX: 0,
  });
  const [isVoiceGestureActive, setIsVoiceGestureActive] = useState(false);
  const [isMicRecording, setIsMicRecording] = useState(false);
  const [isLockedVoiceRecording, setIsLockedVoiceRecording] = useState(false);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [micError, setMicError] = useState("");
  const voiceGestureRef = useRef(voiceGesture);
  const moveGestureRef = useRef(() => {});
  const endGestureRef = useRef(() => {});
  const handleActionClickRef = useRef(null);
  const stopMicCaptureRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartMsRef = useRef(0);
  const gestureStartRef = useRef({ x: 0, y: 0, pointerId: null });
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const navigate = useNavigate();
  const {
    focusChatInputRef,
    blurChatInputRef,
    setIsFocused: setContextFocused,
  } = useChat();
  const { settings, demoOptions } = useSettings();

  // Remove auto-focus - let user explicitly focus the input
  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, []);

  // Add effect to manage app opacity when input is focused instead of response visible
  useEffect(() => {
    if (isFocused) {
      document.body.classList.add("chat-response-active");
    } else {
      document.body.classList.remove("chat-response-active");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("chat-response-active");
    };
  }, [isFocused]);

  // Add effect to manage focus state classes
  useEffect(() => {
    if (isFocused) {
      document.body.classList.add("chat-input-focused");
    } else {
      document.body.classList.remove("chat-input-focused");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("chat-input-focused");
    };
  }, [isFocused]);

  // Add effect to re-enable chat bar whenever input mode is exited
  useEffect(() => {
    if (!isFocused) {
      setIsChatBarDisabled(false);
    }
  }, [isFocused]);

  // Expose focus function to context
  useEffect(() => {
    focusChatInputRef.current = () => {
      if (inputRef.current && !isChatBarDisabled) {
        inputRef.current.focus();
        setIsFocused(true);
      }
    };
    blurChatInputRef.current = () => {
      if (inputRef.current) {
        inputRef.current.blur();
        setIsFocused(false);
        setIsChatBarDisabled(false);
      }
    };
  }, [focusChatInputRef, blurChatInputRef, isChatBarDisabled]);

  // Sync focus state with context
  useEffect(() => {
    setContextFocused(isFocused);
  }, [isFocused, setContextFocused]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current && messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop =
        messagesScrollRef.current.scrollHeight;
    }
  }, [messageHistory, response]);

  // Auto-navigate to iframe when reply has "Live talk" URL
  useEffect(() => {
    if (
      response?.url?.url &&
      response?.url?.display_text === "Live talk"
    ) {
      // Navigate to /more page with URL in state
      navigate("/more", { state: { url: response.url.url } });
      // Exit chat mode
      setIsVisible(false);
      setResponse(null);
      setIsFocused(false);
      setIsChatBarDisabled(false);
      setMessageHistory([]);
    }
  }, [response, navigate]);

  // Function to decode JWT token and extract tenant from Alias claim
  const getTenantFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.Alias;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Function to play beep sound
  const playBeep = () => {
    try {
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Beep frequency in Hz
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.1,
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.error("Error playing beep sound:", error);
    }
  };

  const formatRecordingTimer = (elapsedMs) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  // Function to convert basic Markdown to HTML
  const formatMarkdown = (text) => {
    if (!text) return "";

    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // **bold**
      .replace(/\*(.*?)\*/g, "<em>$1</em>") // *italic*
      .replace(/`(.*?)`/g, "<code>$1</code>") // `code`
      .replace(/\n/g, "<br>"); // line breaks
  };

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    // Store message and clear input immediately
    const messageToSend = message.trim();
    setMessage(""); // Clear input immediately without waiting for response
    setIsLoading(true);
    setIsSendingTextMessage(true);
    setResponse(null);

    // Add user message to history
    const userMessage = `U:${messageToSend}`;
    // Add placeholder AI message immediately
    const placeholderMessage = `A:...`;
    const updatedHistory = [...messageHistory, userMessage, placeholderMessage];
    setMessageHistory(updatedHistory); // Update history immediately to show placeholder

    try {
      // Get token from settings based on selected demo
      const currentDemo = demoOptions.find(
        (demo) => demo.value === settings.demo,
      );
      const token = currentDemo?.token || "";

      // Extract tenant from token
      const tenant = getTenantFromToken(token);
      if (!tenant) {
        throw new Error("Failed to extract tenant from token");
      }

      // Call backend API with WhatsApp message format
      const response = await fetch(`https://${tenant}.looc.ai/api/uai/msg`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          type: "text",
          text: {
            body: messageToSend,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();

      // Check if response is successful (status: 0)
      if (data.status !== 0 || !data.data) {
        throw new Error("Invalid response format");
      }

      const responseData = data.data;
      const aiResponse =
        responseData.msg || "Sorry, I couldn't process your request.";

      // Extract navigation suggestion
      const navigationMatch = aiResponse.match(/\[NAVIGATE:(\w+)\]/);
      const navigationSection = navigationMatch ? navigationMatch[1] : null;

      // Extract connect message
      const connectMatch = aiResponse.match(/\[CONNECT:(.*?)\]/);
      const connectMessage = connectMatch ? connectMatch[1] : null;

      // Clean response by removing both navigation and connect tags
      const cleanResponse = aiResponse
        .replace(/\[NAVIGATE:\w+\]/, "")
        .replace(/\[CONNECT:.*?\]/, "")
        .trim();

      // Replace placeholder with actual AI response
      const aiMessage = `A:${cleanResponse}`;
      const finalHistory = [...updatedHistory.slice(0, -1), aiMessage]; // Remove placeholder and add real response
      setMessageHistory(finalHistory);

      setResponse({
        text: cleanResponse,
        header: responseData.header,
        footer: responseData.footer,
        img: responseData.img,
        actionsTitle: responseData.actionsTitle,
        cards: responseData.cards,
        actions: responseData.actions,
        type: responseData.type,
        voice: responseData.voice,
        url: responseData.url,
        location: responseData.location,
        navigationSection: navigationSection,
        connectMessage: connectMessage,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling chat API:", error);
      const errorResponse =
        "Sorry, I'm having trouble connecting. Please try again later.";

      // Replace placeholder with error response
      const aiMessage = `A:${errorResponse}`;
      const finalHistory = [...updatedHistory.slice(0, -1), aiMessage]; // Remove placeholder and add error response
      setMessageHistory(finalHistory);

      setResponse({
        text: errorResponse,
        navigationSection: null,
        connectMessage: null,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on error reply

      // Don't clear input on error - user can retry with same message
    } finally {
      setIsLoading(false);
      setIsSendingTextMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = async (cmd) => {
    if (!cmd || isLoading) return;

    setIsLoading(true);
    setResponse(null);

    // Add user message to history
    const userMessage = `U:${cmd}`;
    // Add placeholder AI message immediately
    const placeholderMessage = `A:...`;
    const updatedHistory = [...messageHistory, userMessage, placeholderMessage];
    setMessageHistory(updatedHistory); // Update history immediately to show placeholder

    try {
      // Get token from settings based on selected demo
      const currentDemo = demoOptions.find(
        (demo) => demo.value === settings.demo,
      );
      const token = currentDemo?.token || "";

      // Extract tenant from token
      const tenant = getTenantFromToken(token);
      if (!tenant) {
        throw new Error("Failed to extract tenant from token");
      }

      // Call backend API with WhatsApp message format
      const response = await fetch(`https://${tenant}.looc.ai/api/uai/msg`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          type: "text",
          text: {
            body: cmd,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();

      // Check if response is successful (status: 0)
      if (data.status !== 0 || !data.data) {
        throw new Error("Invalid response format");
      }

      const responseData = data.data;
      const aiResponse =
        responseData.msg || "Sorry, I couldn't process your request.";

      // Extract navigation suggestion
      const navigationMatch = aiResponse.match(/\[NAVIGATE:(\w+)\]/);
      const navigationSection = navigationMatch ? navigationMatch[1] : null;

      // Extract connect message
      const connectMatch = aiResponse.match(/\[CONNECT:(.*?)\]/);
      const connectMessage = connectMatch ? connectMatch[1] : null;

      // Clean response by removing both navigation and connect tags
      const cleanResponse = aiResponse
        .replace(/\[NAVIGATE:\w+\]/, "")
        .replace(/\[CONNECT:.*?\]/, "")
        .trim();

      // Replace placeholder with actual AI response
      const aiMessage = `A:${cleanResponse}`;
      const finalHistory = [...updatedHistory.slice(0, -1), aiMessage]; // Remove placeholder and add real response
      setMessageHistory(finalHistory);

      setResponse({
        text: cleanResponse,
        header: responseData.header,
        footer: responseData.footer,
        img: responseData.img,
        actionsTitle: responseData.actionsTitle,
        cards: responseData.cards,
        actions: responseData.actions,
        type: responseData.type,
        voice: responseData.voice,
        url: responseData.url,
        location: responseData.location,
        navigationSection: navigationSection,
        connectMessage: connectMessage,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling chat API:", error);
      const errorResponse =
        "Sorry, I'm having trouble connecting. Please try again later.";

      // Replace placeholder with error response
      const aiMessage = `A:${errorResponse}`;
      const finalHistory = [...updatedHistory.slice(0, -1), aiMessage]; // Remove placeholder and add error response
      setMessageHistory(finalHistory);

      setResponse({
        text: errorResponse,
        navigationSection: null,
        connectMessage: null,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on error reply
    } finally {
      setIsLoading(false);
    }
  };

  handleActionClickRef.current = handleActionClick;

  const handleNavigate = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (response?.navigationSection) {
      const routes = {
        home: "/",
        beneficiaries: "/beneficiaries",
        cards: "/cards",
      };

      const route = routes[response.navigationSection];
      if (route) {
        navigate(route);
        // Don't clear response - keep buttons visible
        setIsFocused(false); // Exit input mode
      }
    }
  };

  const handleConnect = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    setIsLoading(true);

    try {
      // Call the assist API endpoint
      const apiResponse = await fetch(`${apiBaseUrl}/api/ai/assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          msg: response.connectMessage,
        }),
      });

      if (!apiResponse.ok) {
        throw new Error("Failed to get assist response");
      }

      const data = await apiResponse.json();
      const assistResponse =
        data.message ||
        "Thank you, we have created a ticket for you. Click below to interact now with our specialized representative.";

      // Set the response message and show WhatsApp button
      setResponse({
        text: assistResponse,
        navigationSection: null,
        connectMessage: null,
        showWhatsApp: true,
      });
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling assist API:", error);
      // Fallback to default message on error
      setResponse({
        text: "Thank you, we have created a ticket for you. Click below to interact now with our specialized representative.",
        navigationSection: null,
        connectMessage: null,
        showWhatsApp: true,
      });
      playBeep(); // Play beep sound on error reply
    } finally {
      setIsLoading(false);
    }

    setIsVisible(true);
    setIsChatBarDisabled(true); // Disable the chat bar
  };

  const handleWhatsAppClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    window.open("https://wa.me/18337000012", "_blank");
    // Don't clear response - keep buttons visible
  };

  const handleUrlClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (response?.url?.url) {
      // Check if URL starts with map.abuzz.tech
      if (response.url.url.startsWith("https://map.abuzz.tech/")) {
        // Navigate to /more page with URL in state (doesn't alter address bar)
        navigate("/more", { state: { url: response.url.url } });
        // Close the response card and chat messages view
        setIsVisible(false);
        setResponse(null);
        setIsFocused(false);
        setIsChatBarDisabled(false);
        setMessageHistory([]); // Clear message history to close the chat window
      } else {
        // Default behavior: open in new window
        window.open(response.url.url, "_blank");
      }
    }
  };

  const closeResponse = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsVisible(false);
    setResponse(null);
    setIsFocused(false);
    setIsChatBarDisabled(false); // Re-enable chat bar when closing response
    setMessageHistory([]); // Clear message history to close the chat window
  };

  const handleCloseCard = (cardIndex) => (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setResponse((prevResponse) => {
      if (prevResponse?.cards && Array.isArray(prevResponse.cards)) {
        const updatedCards = prevResponse.cards.filter(
          (_, index) => index !== cardIndex,
        );
        return {
          ...prevResponse,
          cards: updatedCards.length > 0 ? updatedCards : null,
        };
      }
      return prevResponse;
    });
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    setIsFocused(false);
    setIsChatBarDisabled(false); // Re-enable chat bar when exiting input mode
    if (isVisible) {
      setIsVisible(false);
      setResponse(null);
    }
  };

  const handleChatAreaClick = (e) => {
    e.stopPropagation();
  };

  // Add specific handlers for buttons to prevent propagation
  const handleButtonClick = (originalHandler) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    originalHandler(e);
  };

  const getNearestAction = (dragUp) => {
    // With column-reverse: visual order is lock(bottom), talk(middle), call(top)
    // Array: [lock(y:56), talk(y:112), call(y:168)]
    // The y values represent drag distances: lock=56 (bottom), talk=112 (middle), call=168 (top)
    return VOICE_ACTIONS.reduce(
      (closest, action) => {
        const distance = Math.abs(dragUp - action.y);
        if (distance < closest.distance) {
          return { id: action.id, distance };
        }
        return closest;
      },
      { id: null, distance: Number.POSITIVE_INFINITY },
    );
  };

  const resetVoiceGesture = () => {
    setIsVoiceGestureActive(false);
    setVoiceGesture({
      mode: "idle",
      intent: null,
      selectedAction: null,
      dragUp: 0,
      dragX: 0,
    });
    gestureStartRef.current = { x: 0, y: 0, pointerId: null };
  };

  const stopActiveMediaStream = () => {
    if (!mediaStreamRef.current) return;
    mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const startMicCapture = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicError("Microphone is not supported in this browser.");
      return false;
    }

    try {
      setMicError("");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const candidateMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ];
      const mimeType = candidateMimeTypes.find((type) =>
        window.MediaRecorder?.isTypeSupported?.(type),
      );
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        stopActiveMediaStream();
      };

      mediaRecorderRef.current = recorder;
      recordingStartMsRef.current = Date.now();
      setRecordingElapsedMs(0);
      recorder.start(200);
      setIsMicRecording(true);
      return true;
    } catch (error) {
      console.error("Failed to start microphone capture:", error);
      setMicError("Microphone permission is required.");
      stopActiveMediaStream();
      return false;
    }
  };

  const stopMicCapture = async ({ discard = false } = {}) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setIsMicRecording(false);
      return null;
    }

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const durationMs = Date.now() - recordingStartMsRef.current;
        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopActiveMediaStream();
        setIsMicRecording(false);
        setRecordingElapsedMs(0);

        if (discard || blob.size === 0) {
          resolve(null);
          return;
        }

        resolve({
          blob,
          durationMs,
          mimeType,
        });
      };

      if (recorder.state === "inactive") {
        recorder.onstop();
      } else {
        recorder.stop();
      }
    });
  };
  stopMicCaptureRef.current = stopMicCapture;

  const handleVoiceGestureStart = async (e) => {
    if (
      message.trim() ||
      isLoading ||
      isChatBarDisabled ||
      isLockedVoiceRecording
    )
      return;

    e.preventDefault();
    e.stopPropagation();

    gestureStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      pointerId: e.pointerId,
    };

    const recordingStarted = await startMicCapture();
    if (!recordingStarted) {
      resetVoiceGesture();
      return;
    }

    setIsVoiceGestureActive(true);
    setVoiceGesture({
      mode: "recording", // Start in recording mode, not selection mode
      intent: null,
      selectedAction: null, // No preselection
      dragUp: 0,
      dragX: 0,
    });
  };

  useEffect(() => {
    voiceGestureRef.current = voiceGesture;
  }, [voiceGesture]);

  useEffect(() => {
    if (!isMicRecording) return undefined;

    const updateElapsed = () => {
      setRecordingElapsedMs(Date.now() - recordingStartMsRef.current);
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isMicRecording]);

  useEffect(() => {
    moveGestureRef.current = (e) => {
      if (!isVoiceGestureActive || voiceGestureRef.current.mode === "idle")
        return;

      const { x, y } = gestureStartRef.current;
      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;
      const dragUp = Math.max(0, -deltaY);
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      let nextIntent = voiceGestureRef.current.intent;
      let nextMode = voiceGestureRef.current.mode;
      let nextSelectedAction = voiceGestureRef.current.selectedAction;

      if (!nextIntent && Math.max(absX, absY) > GESTURE_THRESHOLDS.intentLock) {
        nextIntent = absY > absX ? "vertical" : "horizontal";
      }

      // Check for cancel:
      // 1) hard left swipe, OR
      // 2) move back down while left, OR
      // 3) hover in the cancel track area near the base before release.
      const isSwipingLeft = deltaX < -GESTURE_THRESHOLDS.cancelLeft;
      const isSwipingBackDown = deltaY > 20; // Positive deltaY means moving down
      const isMovingLeftWhileDown = isSwipingBackDown && deltaX < -20;
      const isHoveringCancelTrack = deltaX < -26 && deltaY > -18;

      if (
        (nextIntent === "horizontal" && isSwipingLeft) ||
        isMovingLeftWhileDown ||
        isHoveringCancelTrack
      ) {
        nextMode = "cancel";
      } else if (nextIntent === "vertical" || nextMode === "selection") {
        if (dragUp > GESTURE_THRESHOLDS.enterSelection) {
          nextMode = "selection";
        } else {
          // Don't enter selection mode until dragUp reaches threshold
          nextMode = "recording";
          nextSelectedAction = null;
        }
      }

      if (nextMode === "selection") {
        // Only select when dragUp is close enough to a button's y value
        const nearest = getNearestAction(dragUp);
        const threshold = 30; // Distance threshold to consider a button "reached"
        if (nearest.distance <= threshold) {
          nextSelectedAction = nearest.id;
        } else {
          nextSelectedAction = null;
        }
      } else {
        nextSelectedAction = null;
      }

      setVoiceGesture((prev) => ({
        ...prev,
        mode: nextMode,
        intent: nextIntent,
        selectedAction: nextSelectedAction,
        dragUp,
        dragX: deltaX,
      }));
    };

    endGestureRef.current = async () => {
      if (!isVoiceGestureActive) return;

      const { mode, selectedAction, intent } = voiceGestureRef.current;
      resetVoiceGesture();

      if (mode === "cancel") {
        await stopMicCaptureRef.current?.({ discard: true });
        return;
      }
      if (mode === "selection" || intent === "vertical" || !!selectedAction) {
        if (selectedAction === "lock") {
          setIsLockedVoiceRecording(true);
          return;
        }

        // Any interaction with the vertical action rail must never send voice note.
        await stopMicCaptureRef.current?.({ discard: true });

        if (selectedAction === "call") {
          handleActionClickRef.current?.("Please call me now on the phone");
          return;
        }
        if (selectedAction === "talk") {
          handleActionClickRef.current?.("I want to connect live please");
          return;
        }

        return;
      }

      // If no action selected (or mode is recording), send voice note
      const voiceNote = await stopMicCaptureRef.current?.({ discard: false });
      if (voiceNote) {
        // Ignore recordings shorter than 1000ms
        if (voiceNote.durationMs < 1000) {
          return;
        }
        const durationSeconds = Math.max(
          1,
          Math.round(voiceNote.durationMs / 1000),
        );
        handleActionClickRef.current?.(`Voice note (${durationSeconds}s)`);
      }
    };
  }, [isVoiceGestureActive]);

  useEffect(() => {
    if (!isVoiceGestureActive) return undefined;

    const onPointerMove = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      event.preventDefault();
      moveGestureRef.current(event);
    };

    const onPointerUp = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      endGestureRef.current();
    };

    const onPointerCancel = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      resetVoiceGesture();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
    window.addEventListener("pointercancel", onPointerCancel, {
      passive: false,
    });

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [isVoiceGestureActive]);

  const cancelProgress = Math.min(
    1,
    Math.max(0, -voiceGesture.dragX / GESTURE_THRESHOLDS.cancelLeft),
  );
  const upProgress = Math.min(
    1,
    Math.max(0, voiceGesture.dragUp / GESTURE_THRESHOLDS.enterSelection),
  );

  useEffect(() => {
    return () => {
      stopActiveMediaStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
    };
  }, []);

  const handleSendLockedVoice = async () => {
    if (!isLockedVoiceRecording || isLoading) return;

    setIsLockedVoiceRecording(false);
    const voiceNote = await stopMicCaptureRef.current?.({ discard: false });
    if (!voiceNote) return;

    // Ignore recordings shorter than 1000ms
    if (voiceNote.durationMs < 1000) {
      return;
    }

    const durationSeconds = Math.max(
      1,
      Math.round(voiceNote.durationMs / 1000),
    );
    handleActionClickRef.current?.(`Voice note (${durationSeconds}s)`);
  };

  const handleDiscardLockedVoice = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLockedVoiceRecording || isLoading) return;

    setIsLockedVoiceRecording(false);
    resetVoiceGesture();
    await stopMicCaptureRef.current?.({ discard: true });
  };

  return (
    <>
      {/* Overlay OUTSIDE chat-interface so it's actually clickable */}
      {isFocused && (
        <div className="chat-overlay" onClick={handleOverlayClick} />
      )}

      <div className="chat-interface">
        {/* Chat messages area - now inline above input */}
        {messageHistory.length > 0 && (
          <div
            className="chat-messages-container"
            onClick={handleChatAreaClick}
          >
            <button
              className="close-response"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeResponse(e);
              }}
            >
              ×
            </button>
            <div className="chat-messages-scroll" ref={messagesScrollRef}>
              {messageHistory.map((msg, index) => {
                const isUser = msg.startsWith("U:");
                const messageText = msg.substring(2);
                const isPlaceholder = !isUser && messageText === "...";
                return (
                  <div
                    key={index}
                    className={`chat-message ${isUser ? "user-message" : "ai-message"}`}
                  >
                    <div
                      className={`message-content ${isPlaceholder ? "placeholder" : ""}`}
                      dangerouslySetInnerHTML={{
                        __html: formatMarkdown(messageText),
                      }}
                    />
                  </div>
                );
              })}
              {response?.img && (
                <div className="chat-message ai-message">
                  <img
                    src={response.img}
                    alt="Chat response"
                    className="chat-response-image"
                  />
                </div>
              )}
              {response?.navigationSection && (
                <div className="chat-action-buttons">
                  <button
                    className="navigate-button"
                    onClick={handleButtonClick(handleNavigate)}
                  >
                    Go to{" "}
                    {response.navigationSection.charAt(0).toUpperCase() +
                      response.navigationSection.slice(1)}
                  </button>
                </div>
              )}
              {response?.connectMessage && (
                <div className="chat-action-buttons">
                  <button
                    className="connect-button"
                    onClick={handleButtonClick(handleConnect)}
                  >
                    Connect to representative
                  </button>
                </div>
              )}
              {response?.showWhatsApp && (
                <div className="chat-action-buttons">
                  <button
                    className="whatsapp-button"
                    onClick={handleButtonClick(handleWhatsAppClick)}
                  >
                    Chat on WhatsApp
                  </button>
                </div>
              )}
              {response?.url?.display_text && response?.url?.url && (
                <div className="chat-action-buttons">
                  <button
                    className="url-button"
                    onClick={handleButtonClick(handleUrlClick)}
                  >
                    {response.url.display_text}
                  </button>
                </div>
              )}
              {response?.actions &&
                Array.isArray(response.actions) &&
                response.actions.length > 0 && (
                  <div className="chat-action-buttons chat-actions-horizontal">
                    {response.actions.map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        className="action-button"
                        onClick={handleButtonClick(() => {
                          // Special handling for URL with display_text="Live talk"
                          if (
                            response?.url?.url &&
                            response?.url?.display_text === "Live talk"
                          ) {
                            // Navigate to /more page with URL in state
                            navigate("/more", {
                              state: { url: response.url.url },
                            });
                            // Exit chat mode
                            setIsVisible(false);
                            setResponse(null);
                            setIsFocused(false);
                            setIsChatBarDisabled(false);
                            setMessageHistory([]);
                          } else {
                            // Default behavior: call handleActionClick
                            handleActionClick(action.cmd);
                          }
                        })}
                      >
                        {action.title || action.cmd || "Action"}
                      </button>
                    ))}
                  </div>
                )}
              {response?.cards &&
                Array.isArray(response.cards) &&
                response.cards.length > 0 && (
                  <div
                    className="chat-cards-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {response.cards.map((card, cardIndex) => (
                      <div
                        key={cardIndex}
                        className="chat-card"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="card-close-button"
                          onClick={handleButtonClick(
                            handleCloseCard(cardIndex),
                          )}
                        >
                          ×
                        </button>
                        {card.img && (
                          <img
                            src={card.img}
                            alt={card.title || "Card"}
                            className="card-image"
                          />
                        )}
                        {card.title && (
                          <div className="card-title">{card.title}</div>
                        )}
                        {card.text && (
                          <div className="card-text">{card.text}</div>
                        )}
                        {card.actions &&
                          Array.isArray(card.actions) &&
                          card.actions.length > 0 && (
                            <div className="card-actions">
                              {card.actions.map((action, actionIndex) => (
                                <button
                                  key={actionIndex}
                                  className="card-action-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (action.url) {
                                      // Check if URL starts with map.abuzz.tech
                                      if (
                                        action.url.startsWith(
                                          "https://map.abuzz.tech/",
                                        )
                                      ) {
                                        // Navigate to /more page with URL in state (doesn't alter address bar)
                                        navigate("/more", {
                                          state: { url: action.url },
                                        });
                                        // Close the card and chat messages view
                                        handleCloseCard(cardIndex)(e);
                                        setMessageHistory([]); // Clear message history to close the chat window
                                        setIsVisible(false);
                                        setResponse(null);
                                        setIsFocused(false);
                                        setIsChatBarDisabled(false);
                                      } else {
                                        // Default behavior: open in new window
                                        window.open(action.url, "_blank");
                                      }
                                    }
                                  }}
                                >
                                  {action.title || action.text || "Action"}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Chat input bar */}
        {isChatBarVisible && (
          <div
            className={`chat-input-container ${isFocused ? "focused" : ""} ${
              isChatBarDisabled ? "disabled" : ""
            }`}
            onClick={isChatBarDisabled ? undefined : handleChatAreaClick}
          >
            <div className="chat-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={
                  isChatBarDisabled
                    ? undefined
                    : (e) => setMessage(e.target.value)
                }
                onKeyPress={isChatBarDisabled ? undefined : handleKeyPress}
                onFocus={isChatBarDisabled ? undefined : handleInputFocus}
                placeholder="Ask me anything..."
                className="chat-input"
                disabled={isLoading || isChatBarDisabled}
              />
              <div
                onClick={
                  isChatBarDisabled
                    ? undefined
                    : isLockedVoiceRecording
                      ? handleButtonClick(handleSendLockedVoice)
                      : !message.trim()
                        ? undefined
                        : handleButtonClick(handleSend)
                }
                onPointerDown={handleVoiceGestureStart}
                className={`send-button ${
                  isLoading || isChatBarDisabled ? "disabled" : ""
                } ${isVoiceGestureActive ? "recording" : ""} ${
                  isMicRecording ? "mic-live" : ""
                } ${isLockedVoiceRecording ? "locked-send" : ""}`}
              >
                <span className="send-icon">
                  {isLoading && !isSendingTextMessage ? (
                    <img
                      src="/looc.png"
                      alt="Loading"
                      className="loading-spinner"
                    />
                  ) : isLockedVoiceRecording ? (
                    "➤"
                  ) : isVoiceGestureActive ? (
                    <img
                      src="/looc.png"
                      alt="Recording"
                      className="loading-spinner"
                    />
                  ) : message.trim() ? (
                    "➤"
                  ) : (
                    <MicIcon />
                  )}
                </span>
              </div>
            </div>
            {isMicRecording && (
              <div
                className={`voice-gesture-shell ${voiceGesture.mode === "cancel" ? "cancel-ready" : ""}`}
              >
                <div
                  className={`voice-cancel-track ${isLockedVoiceRecording ? "locked" : ""}`}
                  style={{ "--cancel-progress": cancelProgress }}
                >
                  <div className="voice-recording-status">
                    {isLockedVoiceRecording && (
                      <button
                        type="button"
                        className="voice-recording-trash-button"
                        onClick={handleDiscardLockedVoice}
                        aria-label="Discard recording"
                        title="Discard recording"
                      >
                        <span className="voice-recording-trash">🗑</span>
                      </button>
                    )}
                    <span className="voice-recording-dot" />
                    <span className="voice-recording-timer">
                      {formatRecordingTimer(recordingElapsedMs)}
                    </span>
                  </div>
                  <span className="voice-cancel-label">
                    {isLockedVoiceRecording
                      ? "Recording locked"
                      : voiceGesture.mode === "cancel"
                        ? "Release to cancel"
                        : "Slide left to cancel"}
                  </span>
                </div>
                {isVoiceGestureActive && (
                  <div
                    className="voice-up-indicator"
                    style={{ "--up-progress": upProgress }}
                  >
                    <span className="voice-up-arrow">
                      <ChevronIcon />
                    </span>
                  </div>
                )}
                {isVoiceGestureActive && (
                  <div className="voice-action-rail active">
                    <div className="voice-action-items">
                      <div className="voice-action-chevron">
                        <span className="voice-action-icon">
                          <ChevronIcon />
                        </span>
                      </div>
                      {VOICE_ACTIONS.map((action) => {
                        const IconComponent = action.icon;
                        return (
                          <div
                            key={action.id}
                            className={`voice-action-item ${
                              voiceGesture.selectedAction === action.id &&
                              voiceGesture.mode !== "cancel"
                                ? "selected"
                                : ""
                            }`}
                          >
                            <span className="voice-action-icon">
                              <IconComponent />
                            </span>
                            <span className="voice-action-label">
                              {action.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {micError && <div className="voice-action-error">{micError}</div>}
          </div>
        )}
      </div>
    </>
  );
};

export default ChatInterface;
